import { Component, computed, effect, EffectRef, ElementRef, HostListener, inject, Signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ButtonType } from '../../../../../models/enums/components/Button/ButtonType.enum';
import { Search } from '../../../../../models/interfaces/components/Search.interface';
import { Configuration } from '../../../../../models/interfaces/configuration/index.interface';
import { ButtonColors } from '../../../../../models/enums/components/Button/ButtonColor.enum';
import { CommonModule } from '@angular/common';
import { SearchAdvancedComponent } from './search-advanced/search-advanced.component';
import { SearchSimpleComponent } from './search-simple/search-simple.component';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { SearchTypes } from '../../../../../models/enums/components/Search/SearchTypes.enum';
import { SearchOption } from '../../../../../models/types/components/Search.type';
import { SearchService } from '../../../../../services/components/search.service';
import anime from 'animejs';
import { LayoutService } from '@services/layout.service';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    SearchSimpleComponent,
    SearchAdvancedComponent
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css'
})
export class SearchComponent implements Search {

  // VIEW CHILDS
  @ViewChild('searchContainer')
  private searchContainer!:       ElementRef<HTMLDivElement>;

  @ViewChild('searchBtn',{read: ElementRef})
  private searchButton!:          ElementRef<HTMLElement>;

  
  // SERVICES
  private readonly SearchService: SearchService                           = inject(SearchService);
  private readonly layoutService: LayoutService                           = inject(LayoutService);
  private readonly view:          Signal<AppViews|null>                   = this.SearchService.view;
  private readonly isMobile:      Signal<boolean>                         = this.layoutService.isMobile;

  
  // CONFIGURATION
  private readonly searchConfig:  Signal<Configuration['Ui']['Search']>   = this.SearchService.searchConfig;
  public  readonly hasSearch:     Search['hasSearch']                     = computed((): boolean => this.searchConfig().hasSearch);
  public  readonly type:          Search['type']                          = computed((): SearchTypes  => this.searchConfig().type ?? SearchTypes.Simple);
  public  readonly order:         Search['order']                         = computed((): 'JSON'|'alphabetical' => this.searchConfig().order ?? 'JSON');
  public  readonly fileData:      Search['fileData']                      = computed((): Record<string, Record<string, string[]>> => this.searchConfig().fileData ?? {});


  // GLOBAL STATE
  public  readonly searchActive:  Signal<boolean|null>                    = this.SearchService.searchActive;
  public  readonly searchValue:   Signal<SearchOption|null>               = this.SearchService.searchValue;                           
  protected readonly isAnimating: WritableSignal<boolean>                 = this.SearchService.isAnimating;
  private readonly animation:     number                                  = this.SearchService.duration;

  
  // COMPONENT STATE
  public  showSearch:             boolean                                 = false;
  public  hideSearchByView:       boolean                                 = true;
  
 
  // BUTTONS
  public readonly buttonType:     ButtonType                              = ButtonType.Pill;
  public readonly buttonColors:   typeof ButtonColors                     = ButtonColors;

  // SEARCHERS
  public readonly searchTypes:    typeof SearchTypes                      = SearchTypes;
  

  // ACTIONS

 @HostListener("window:resize")
  onResize() {
    const button: HTMLElement | undefined = this.searchButton?.nativeElement.childNodes[0] as HTMLElement;

    if (!button || !this.searchActive() || !this.isMobile() ) return;

    if (this.isAnimating()) return;

    const translateX = window.innerWidth - 60; 
    button.style.transform = `translateX(${translateX}px)`;
    
  }

  /**
   * Método que activa o desactiva el Search.
   * @returns {void}
   */
  public toggleSearchButton(): void {
    this.SearchService.toggleSearch()
  }

  /**
   * Método asociado al botón de búsqueda.
   * Si el searcher está vacío, este método actua como toggleSearchButton.
   * Si el searcher tiene un valor, navega a la sección buscada. Si es
   * @returns 
   */
  public navigateTo(): void {

    if(this.searchActive()) return;
    if(!this.searchValue() || !this.searchActive()) return this.toggleSearchButton();

    setTimeout((): void => {

      this.SearchService.navigateToSearch(this.searchValue()!);

    }, this.animation + 250);

  }
  
  private searchVisbility(show: boolean): void {

    if(this.hideSearchByView && show) this.hideSearchByView = false; 

    anime({
      targets: this.searchContainer?.nativeElement,
      opacity: show ? 1 : 0,
      easing: show ? 'easeInOutSine' : 'easeInOutSine',
      duration: 300,
      complete: (): boolean => this.hideSearchByView = !show
    })

  }

  // EFFECTS
  /**
   * Efecto que reacciona al cambio de la signal searchActive.
   * Si el valor es null, no hace nada.
   * Si no, lanza la animación en función del valor de searchActive. (si es true se muestra, si es false se oculta)
   */
  public animateSearch: EffectRef = effect((): void => {

    const isActive:   boolean | null  = this.searchActive()!,
          container:  HTMLElement     = this.searchContainer?.nativeElement,
          button:     HTMLElement     = this.searchButton?.nativeElement.childNodes[0] as HTMLElement;

    if(isActive === null || (!container || !button)) return;

    if(isActive) this.showSearch = true;

    // Al comenzar y finalizar la animación, el estado de isAnimating se actualiza.
    // Si active es false, al finalizar se establece showSearch a false para aplicar 'hidden' al contenedor. (display: none)
    anime.timeline({
      duration: this.animation,
      begin:    (): void => this.isAnimating.set(true),
      complete: (): void => {
        if(!isActive) this.showSearch = false; 
        this.isAnimating.set(false);
      }
    })
    // CONTAINER OPACITY
    .add({
      targets: container,
      easing: 'easeInOutSine',
      opacity: isActive ? 1 : 0,
    })
    // BUTTON ANIMATION
    .add({
      begin: ()=> {
        return isActive ? 
          button.classList.add(ButtonColors.White) : 
          button.classList.remove(ButtonColors.White);
      },
      targets: button,
      easing: 'easeInOutSine',
      translateX: !isActive ? 
        0 : 
        this.isMobile() ?
        window.innerWidth - 60 :
        this.type() === SearchTypes.Simple ? '310px' : '365px',
    })


  });


  public toggleSearchVisibility: EffectRef = effect((): void => {

    const view: AppViews | null = this.view();

    if(view === null) return;
    
    if(untracked(this.searchActive)) this.SearchService.toggleSearch();

    switch(view) {

      case AppViews.Topview:
      case AppViews.Main:
        
        this.searchVisbility(true);

      break;

      case AppViews.Seatmap: 
      case AppViews.Pano:

        this.searchVisbility(false);
        
      break;

    }

  },{allowSignalWrites: true});

}
